import katex from 'katex';
import 'katex/dist/katex.min.css';
import { marked, type Token, type Tokens } from 'marked';
import type { Attachment, AttachmentKind } from './types';

type RenderContext = {
  attachments: Map<string, Attachment>;
  headings: Array<{ depth: number; id: string; text: string }>;
  headingIndex: number;
  onImageClick: (url: string) => void;
  onAttachmentClick: (attachment: Attachment) => void;
  tocRendered: boolean;
};

const MATH_LANGUAGES = new Set(['math', 'katex', 'latex', 'tex']);

export function deriveTitle(markdown: string): string {
  const line = markdown.split(/\r?\n/).find((item) => item.trim())?.trim() ?? '';
  const plain = line
    .replace(/^#{1,6}\s+/, '')
    .replace(/^>\s*/, '')
    .replace(/^[-*+]\s+/, '')
    .replace(/^\d+[.)]\s+/, '')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_~`]/g, '')
    .trim();
  return (plain || '无题日记').slice(0, 80);
}

export function markdownPreview(markdown: string): string {
  return markdown
    .replace(/\[!\[([^\]]*)\]\([^)]+\)\]\([^)]+\)/g, (_, alt: string) => `📷 ${alt || '图片'}`)
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, (_, alt: string) => `📷 ${alt || '图片'}`)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^(?:#{1,6}|>|[-*+]|\d+[.)])\s*/gm, '')
    .replace(/[*_~`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function pendingAttachmentToken(id: string): string {
  return `shiguang-attachment:${id}`;
}

export function insertMarkdownAttachment(text: string, start: number, end: number, token: string, name: string, kind: AttachmentKind): { text: string; caret: number } {
  const before = text.slice(0, start);
  const after = text.slice(end);
  const prefix = before && !before.endsWith('\n') ? '\n\n' : '';
  const suffix = after && !after.startsWith('\n') ? '\n\n' : '\n';
  const safeName = name.replace(/[\[\]]/g, '_');
  const marker = kind === 'image' ? `![${safeName}](${token})` : `[📎 ${safeName}](${token})`;
  const markdown = `${prefix}${marker}${suffix}`;
  return { text: before + markdown + after, caret: before.length + markdown.length };
}

export function removeMarkdownAttachment(text: string, source: string): string {
  const escaped = source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(new RegExp(`\\n*(?:!\\[[^\\]]*\\]|\\[[^\\]]*\\])\\(${escaped}\\)\\n*`, 'g'), '\n\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function replaceMarkdownAttachment(text: string, source: string, replacement: string): string {
  return text.replaceAll(`](${source})`, `](${replacement})`);
}

export function renderMarkdown(markdown: string, attachments: Attachment[], onImageClick: (url: string) => void, onAttachmentClick: (attachment: Attachment) => void = () => undefined): DocumentFragment {
  const source = normalizeExtensions(markdown);
  const tokens = marked.lexer(source, { gfm: true, breaks: true });
  const headings = collectHeadings(tokens);
  const context: RenderContext = {
    attachments: new Map(attachments.map((attachment) => [attachment.url, attachment])),
    headings,
    headingIndex: 0,
    onImageClick,
    onAttachmentClick,
    tocRendered: false,
  };
  const fragment = document.createDocumentFragment();
  appendBlockTokens(fragment, tokens, context);
  if (!fragment.childNodes.length) {
    const empty = document.createElement('p');
    empty.textContent = '这一天没有留下文字。';
    fragment.append(empty);
  }
  return fragment;
}

function normalizeExtensions(markdown: string): string {
  return markdown
    .replace(/^(\s*)\[========\]\s*$/gm, '$1<!--shiguang-page-break-->')
    .replace(/^\s*\[TOCM?\]\s*$/gim, '<!--shiguang-toc-->');
}

function collectHeadings(tokens: Token[]): Array<{ depth: number; id: string; text: string }> {
  const used = new Map<string, number>();
  const result: Array<{ depth: number; id: string; text: string }> = [];
  for (const token of tokens) {
    if (token.type !== 'heading') continue;
    const headingToken = token as Tokens.Heading;
    const text = inlinePlainText(headingToken.tokens).trim();
    const base = slugify(text) || `section-${result.length + 1}`;
    const count = (used.get(base) ?? 0) + 1;
    used.set(base, count);
    result.push({ depth: token.depth, id: count === 1 ? base : `${base}-${count}`, text });
  }
  return result;
}

function slugify(value: string): string {
  return value.toLocaleLowerCase().trim().replace(/[^\p{Letter}\p{Number}]+/gu, '-').replace(/^-|-$/g, '').slice(0, 72);
}

function appendBlockTokens(parent: Node, tokens: Token[], context: RenderContext): void {
  for (const token of tokens) {
    if (token.type === 'space' || token.type === 'def') continue;
    if (token.type === 'heading') {
      const headingToken = token as Tokens.Heading;
      const heading = document.createElement(`h${headingToken.depth}`);
      const meta = context.headings[context.headingIndex++];
      if (meta) heading.id = meta.id;
      appendInlineTokens(heading, headingToken.tokens, context);
      parent.appendChild(heading);
      continue;
    }
    if (token.type === 'paragraph') {
      const paragraphToken = token as Tokens.Paragraph;
      const marker = paragraphToken.text.trim();
      if (marker === '<!--shiguang-toc-->') {
        if (!context.tocRendered) parent.appendChild(renderToc(context.headings));
        context.tocRendered = true;
        continue;
      }
      if (/^\$\$[\s\S]+\$\$$/.test(marker)) {
        parent.appendChild(renderMath(marker.slice(2, -2), true));
        continue;
      }
      const paragraph = document.createElement('p');
      appendInlineTokens(paragraph, paragraphToken.tokens, context);
      parent.appendChild(paragraph);
      continue;
    }
    if (token.type === 'text') {
      const paragraph = document.createElement('p');
      appendInlineTokens(paragraph, token.tokens ?? [token], context);
      parent.appendChild(paragraph);
      continue;
    }
    if (token.type === 'blockquote') {
      const quoteToken = token as Tokens.Blockquote;
      const quote = document.createElement('blockquote');
      appendBlockTokens(quote, quoteToken.tokens, context);
      parent.appendChild(quote);
      continue;
    }
    if (token.type === 'list') {
      parent.appendChild(renderList(token as Tokens.List, context));
      continue;
    }
    if (token.type === 'table') {
      parent.appendChild(renderTable(token as Tokens.Table, context));
      continue;
    }
    if (token.type === 'code') {
      parent.appendChild(renderCode(token as Tokens.Code));
      continue;
    }
    if (token.type === 'hr') {
      parent.appendChild(document.createElement('hr'));
      continue;
    }
    if (token.type === 'html') {
      const markerText = token.text.replace(/\s+/g, '');
      if (markerText === '<!--shiguang-page-break-->') {
        const rule = document.createElement('hr');
        rule.className = 'markdown-page-break';
        rule.setAttribute('aria-label', '分页符');
        parent.appendChild(rule);
      } else if (/^(?:<!--shiguang-toc-->)+$/.test(markerText)) {
        if (!context.tocRendered) parent.appendChild(renderToc(context.headings));
        context.tocRendered = true;
      } else {
        const pre = document.createElement('pre');
        const code = document.createElement('code');
        code.textContent = token.text.trim();
        pre.appendChild(code);
        parent.appendChild(pre);
      }
    }
  }
}

function renderToc(headings: RenderContext['headings']): HTMLElement {
  const details = document.createElement('details');
  details.className = 'markdown-toc';
  details.open = true;
  const summary = document.createElement('summary');
  summary.textContent = '目录';
  details.appendChild(summary);
  const list = document.createElement('ol');
  for (const heading of headings) {
    const item = document.createElement('li');
    item.dataset.depth = String(heading.depth);
    const link = document.createElement('a');
    link.href = `#${heading.id}`;
    link.textContent = heading.text;
    item.appendChild(link);
    list.appendChild(item);
  }
  details.appendChild(list);
  return details;
}

function renderList(token: Tokens.List, context: RenderContext): HTMLElement {
  const list = document.createElement(token.ordered ? 'ol' : 'ul');
  if (list instanceof HTMLOListElement && typeof token.start === 'number' && token.start !== 1) list.start = token.start;
  if (token.items.some((item) => item.task)) list.classList.add('task-list');
  for (const itemToken of token.items) {
    const item = document.createElement('li');
    if (itemToken.task) {
      item.classList.add('task-list-item');
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = Boolean(itemToken.checked);
      checkbox.disabled = true;
      checkbox.setAttribute('aria-label', itemToken.checked ? '已完成' : '未完成');
      item.appendChild(checkbox);
    }
    appendBlockTokens(item, itemToken.tokens, context);
    list.appendChild(item);
  }
  return list;
}

function renderTable(token: Tokens.Table, context: RenderContext): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'markdown-table-wrap';
  const table = document.createElement('table');
  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  token.header.forEach((cell, index) => headRow.appendChild(renderTableCell('th', cell.tokens, token.align[index], context)));
  head.appendChild(headRow);
  table.appendChild(head);
  const body = document.createElement('tbody');
  for (const row of token.rows) {
    const rowElement = document.createElement('tr');
    row.forEach((cell, index) => rowElement.appendChild(renderTableCell('td', cell.tokens, token.align[index], context)));
    body.appendChild(rowElement);
  }
  table.appendChild(body);
  wrapper.appendChild(table);
  return wrapper;
}

function renderTableCell(tag: 'th' | 'td', tokens: Token[], align: Tokens.Table['align'][number], context: RenderContext): HTMLTableCellElement {
  const cell = document.createElement(tag);
  if (align) cell.style.textAlign = align;
  appendInlineTokens(cell, tokens, context);
  return cell;
}

function renderCode(token: Tokens.Code): HTMLElement {
  const language = token.lang?.trim().toLocaleLowerCase() ?? '';
  if (MATH_LANGUAGES.has(language)) return renderMath(token.text.replace(/\\_/g, '_'), true);
  if (language === 'flow') return renderFlowDiagram(token.text);
  if (language === 'seq' || language === 'sequence') return renderSequenceDiagram(token.text);
  const figure = document.createElement('figure');
  figure.className = 'markdown-code';
  if (language) {
    const label = document.createElement('figcaption');
    label.textContent = language;
    figure.appendChild(label);
  }
  const pre = document.createElement('pre');
  const code = document.createElement('code');
  if (language) code.dataset.language = language;
  code.textContent = token.text;
  pre.appendChild(code);
  figure.appendChild(pre);
  return figure;
}

function appendInlineTokens(parent: HTMLElement, tokens: Token[], context: RenderContext): void {
  let current: HTMLElement = parent;
  const stack: HTMLElement[] = [];
  for (const token of tokens) {
    if (token.type === 'text' || token.type === 'escape') {
      appendTextWithMath(current, token.text);
    } else if (token.type === 'br') {
      current.appendChild(document.createElement('br'));
    } else if (token.type === 'codespan') {
      const code = document.createElement('code'); code.textContent = token.text; current.appendChild(code);
    } else if (token.type === 'strong' || token.type === 'em' || token.type === 'del') {
      const styledToken = token as Tokens.Strong | Tokens.Em | Tokens.Del;
      const element = document.createElement(token.type === 'strong' ? 'strong' : token.type === 'em' ? 'em' : 'del');
      appendInlineTokens(element, styledToken.tokens, context); current.appendChild(element);
    } else if (token.type === 'link') {
      const linkToken = token as Tokens.Link;
      const attachment = context.attachments.get(linkToken.href);
      if (attachment) {
        current.appendChild(attachmentCard(attachment, context.onAttachmentClick));
      } else {
        const url = safeLink(linkToken.href);
        if (!url) { appendInlineTokens(current, linkToken.tokens, context); continue; }
        const anchor = externalAnchor(url); if (linkToken.title) anchor.title = linkToken.title;
        if (linkToken.tokens.length === 1 && linkToken.tokens[0].type === 'image') anchor.className = 'markdown-image-link';
        appendInlineTokens(anchor, linkToken.tokens, context); current.appendChild(anchor);
      }
    } else if (token.type === 'image') {
      const attachment = context.attachments.get(token.href);
      if (attachment && !attachment.available) {
        current.appendChild(deletedAttachment(attachment.name, true));
      } else {
        const url = attachment?.kind === 'image' ? attachment.url : markdownImageUrl(token.href);
        if (!url) { current.appendChild(document.createTextNode(token.text || '图片')); continue; }
        const image = markdownImage(url, token.text); if (token.title) image.title = token.title;
        if (current.tagName !== 'A') image.addEventListener('click', () => context.onImageClick(url));
        current.appendChild(image);
      }
    } else if (token.type === 'html') {
      const tag = parseSafeInlineTag(token.text);
      if (!tag) current.appendChild(document.createTextNode(token.text));
      else if (tag.closing) {
        const target = stack.pop();
        if (target) current = target;
      } else if (tag.name === 'br') {
        current.appendChild(document.createElement('br'));
      } else {
        const element = document.createElement(tag.name);
        if (tag.name === 'abbr' && tag.title) element.title = tag.title;
        current.appendChild(element); stack.push(current); current = element;
      }
    }
  }
}

function appendTextWithMath(parent: HTMLElement, text: string): void {
  const pattern = /\$\$([\s\S]+?)\$\$/g;
  let offset = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > offset) parent.appendChild(document.createTextNode(text.slice(offset, index)));
    parent.appendChild(renderMath(match[1], false));
    offset = index + match[0].length;
  }
  if (offset < text.length) parent.appendChild(document.createTextNode(text.slice(offset)));
}

function parseSafeInlineTag(value: string): { name: 's' | 'sub' | 'sup' | 'abbr' | 'br'; closing: boolean; title?: string } | null {
  const closing = /^<\/(s|sub|sup|abbr)\s*>$/i.exec(value.trim());
  if (closing) return { name: closing[1].toLocaleLowerCase() as 's' | 'sub' | 'sup' | 'abbr', closing: true };
  const opening = /^<(s|sub|sup|br)\s*\/?>$/i.exec(value.trim());
  if (opening) return { name: opening[1].toLocaleLowerCase() as 's' | 'sub' | 'sup' | 'br', closing: false };
  const abbr = /^<abbr\s+title=(?:"([^"]*)"|'([^']*)')\s*>$/i.exec(value.trim());
  if (abbr) return { name: 'abbr', closing: false, title: abbr[1] ?? abbr[2] ?? '' };
  return null;
}

function inlinePlainText(tokens: Token[]): string {
  return tokens.map((token) => {
    if (token.type === 'image') return token.text;
    if ('tokens' in token && Array.isArray(token.tokens)) return inlinePlainText(token.tokens);
    return 'text' in token && typeof token.text === 'string' ? token.text : '';
  }).join('');
}

function renderMath(expression: string, displayMode: boolean): HTMLElement {
  const element = document.createElement(displayMode ? 'div' : 'span');
  element.className = displayMode ? 'markdown-math markdown-math-block' : 'markdown-math';
  try {
    katex.render(expression.trim().replace(/^\\\(|\\\)$/g, ''), element, { displayMode, throwOnError: false, strict: 'ignore', trust: false });
  } catch {
    element.textContent = expression;
  }
  return element;
}

function renderFlowDiagram(source: string): HTMLElement {
  const container = diagramContainer('流程图');
  const nodes = [...source.matchAll(/^(\w+)=>(start|end|operation|condition):\s*(.+)$/gm)].map((match) => ({ id: match[1], type: match[2], label: match[3] }));
  if (!nodes.length) return fallbackDiagram(container, source);
  const svg = svgElement('svg');
  const width = 620; const gap = 112; const height = Math.max(180, nodes.length * gap + 40); const center = width / 2;
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`); svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', '流程图');
  const defs = svgElement('defs'); const marker = svgElement('marker');
  marker.setAttribute('id', `arrow-${Math.random().toString(36).slice(2)}`); marker.setAttribute('viewBox', '0 0 10 10'); marker.setAttribute('refX', '9'); marker.setAttribute('refY', '5'); marker.setAttribute('markerWidth', '7'); marker.setAttribute('markerHeight', '7'); marker.setAttribute('orient', 'auto-start-reverse');
  const arrow = svgElement('path'); arrow.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z'); marker.appendChild(arrow); defs.appendChild(marker); svg.appendChild(defs);
  const markerId = marker.getAttribute('id') ?? '';
  nodes.forEach((node, index) => {
    const y = 30 + index * gap;
    if (index) drawSvgArrow(svg, center, y - 80, center, y - 32, markerId);
    const shape = node.type === 'condition' ? svgElement('polygon') : node.type === 'start' || node.type === 'end' ? svgElement('ellipse') : svgElement('rect');
    shape.classList.add('diagram-shape');
    if (shape.tagName === 'polygon') shape.setAttribute('points', `${center},${y - 32} ${center + 110},${y} ${center},${y + 32} ${center - 110},${y}`);
    else if (shape.tagName === 'ellipse') { shape.setAttribute('cx', String(center)); shape.setAttribute('cy', String(y)); shape.setAttribute('rx', '105'); shape.setAttribute('ry', '30'); }
    else { shape.setAttribute('x', String(center - 115)); shape.setAttribute('y', String(y - 31)); shape.setAttribute('width', '230'); shape.setAttribute('height', '62'); shape.setAttribute('rx', '12'); }
    svg.appendChild(shape); addSvgText(svg, center, y + 5, node.label);
  });
  const conditionIndex = nodes.findIndex((node) => node.type === 'condition');
  if (conditionIndex >= 0 && conditionIndex + 1 < nodes.length) {
    const conditionY = 30 + conditionIndex * gap;
    addSvgText(svg, center + 20, conditionY + 51, 'Yes');
    addSvgText(svg, center + 142, conditionY + 4, 'No');
    const loop = svgElement('path');
    loop.classList.add('diagram-message');
    loop.setAttribute('d', `M ${center + 110} ${conditionY} L ${center + 165} ${conditionY} L ${center + 165} ${conditionY - gap} L ${center + 116} ${conditionY - gap}`);
    loop.setAttribute('marker-end', `url(#${markerId})`);
    svg.appendChild(loop);
  }
  container.appendChild(svg); return container;
}

function renderSequenceDiagram(source: string): HTMLElement {
  const container = diagramContainer('序列图');
  const events = source.split('\n').map((line) => {
    const message = /^(\w+)\s*(-{1,2}>>?|--?>)\s*(\w+):\s*(.+)$/.exec(line.trim());
    if (message) return { type: 'message' as const, from: message[1], to: message[3], label: message[4] };
    const note = /^Note\s+(right|left)\s+of\s+(\w+):\s*(.+)$/i.exec(line.trim());
    if (note) return { type: 'note' as const, side: note[1].toLocaleLowerCase(), actor: note[2], label: note[3].replace(/\\n/g, ' · ') };
    return null;
  }).filter((event): event is NonNullable<typeof event> => Boolean(event));
  const actors = [...new Set(events.flatMap((event) => event.type === 'message' ? [event.from, event.to] : [event.actor]))];
  if (actors.length < 2 || !events.some((event) => event.type === 'message')) return fallbackDiagram(container, source);
  const width = Math.max(620, actors.length * 230); const height = 150 + events.length * 82; const svg = svgElement('svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`); svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', '序列图');
  const positions = new Map(actors.map((actor, index) => [actor, 90 + index * ((width - 180) / Math.max(1, actors.length - 1))]));
  actors.forEach((actor) => {
    const x = positions.get(actor) ?? 0; const box = svgElement('rect'); box.classList.add('diagram-shape'); box.setAttribute('x', String(x - 62)); box.setAttribute('y', '22'); box.setAttribute('width', '124'); box.setAttribute('height', '42'); box.setAttribute('rx', '10'); svg.appendChild(box); addSvgText(svg, x, 49, actor);
    const line = svgElement('line'); line.classList.add('diagram-life'); line.setAttribute('x1', String(x)); line.setAttribute('x2', String(x)); line.setAttribute('y1', '64'); line.setAttribute('y2', String(height - 20)); svg.appendChild(line);
  });
  events.forEach((event, index) => {
    const y = 105 + index * 82;
    if (event.type === 'note') {
      const actorX = positions.get(event.actor) ?? 0;
      const desiredX = event.side === 'left' ? actorX - 208 : actorX + 18;
      const noteX = desiredX < 12 || desiredX + 190 > width - 12 ? actorX - 208 : desiredX;
      const box = svgElement('rect'); box.classList.add('diagram-note'); box.setAttribute('x', String(Math.max(12, noteX))); box.setAttribute('y', String(y - 27)); box.setAttribute('width', '190'); box.setAttribute('height', '54'); box.setAttribute('rx', '8'); svg.appendChild(box);
      addSvgText(svg, Math.max(12, noteX) + 95, y + 5, event.label); return;
    }
    const from = positions.get(event.from) ?? 0; const to = positions.get(event.to) ?? 0;
    const line = svgElement('line'); line.classList.add('diagram-message'); line.setAttribute('x1', String(from)); line.setAttribute('x2', String(to)); line.setAttribute('y1', String(y)); line.setAttribute('y2', String(y)); svg.appendChild(line);
    const tip = svgElement('polygon'); const direction = to >= from ? -1 : 1; tip.classList.add('diagram-arrow'); tip.setAttribute('points', `${to},${y} ${to + direction * 11},${y - 6} ${to + direction * 11},${y + 6}`); svg.appendChild(tip);
    addSvgText(svg, (from + to) / 2, y - 12, event.label);
  });
  container.appendChild(svg); return container;
}

function diagramContainer(label: string): HTMLElement {
  const figure = document.createElement('figure'); figure.className = 'markdown-diagram';
  const caption = document.createElement('figcaption'); caption.textContent = label; figure.appendChild(caption); return figure;
}

function fallbackDiagram(container: HTMLElement, source: string): HTMLElement {
  const pre = document.createElement('pre'); const code = document.createElement('code'); code.textContent = source; pre.appendChild(code); container.appendChild(pre); return container;
}

function svgElement<K extends keyof SVGElementTagNameMap>(name: K): SVGElementTagNameMap[K] {
  return document.createElementNS('http://www.w3.org/2000/svg', name);
}

function addSvgText(svg: SVGSVGElement, x: number, y: number, value: string): void {
  const text = svgElement('text'); text.setAttribute('x', String(x)); text.setAttribute('y', String(y)); text.setAttribute('text-anchor', 'middle'); text.textContent = value; svg.appendChild(text);
}

function drawSvgArrow(svg: SVGSVGElement, x1: number, y1: number, x2: number, y2: number, markerId: string): void {
  const line = svgElement('line'); line.classList.add('diagram-message'); line.setAttribute('x1', String(x1)); line.setAttribute('y1', String(y1)); line.setAttribute('x2', String(x2)); line.setAttribute('y2', String(y2)); line.setAttribute('marker-end', `url(#${markerId})`); svg.appendChild(line);
}

function markdownImage(url: string, alt: string): HTMLImageElement {
  const image = document.createElement('img'); image.src = url; image.alt = alt || '日记图片'; image.loading = 'lazy'; image.className = 'markdown-image'; return image;
}

function markdownImageUrl(value: string): string | null {
  return externalImageProxyUrl(value);
}

function attachmentCard(attachment: Attachment, onClick: (attachment: Attachment) => void): HTMLElement {
  if (!attachment.available) return deletedAttachment(attachment.name, false);
  const card = document.createElement('button'); card.type = 'button'; card.className = 'markdown-attachment';
  const icon = document.createElement('span'); icon.className = 'markdown-attachment-icon';
  icon.textContent = attachment.kind === 'pdf' ? 'PDF' : attachment.kind === 'markdown' ? 'MD' : attachment.kind === 'text' ? 'TXT' : attachment.kind === 'archive' ? 'ZIP' : 'FILE';
  const copy = document.createElement('span'); copy.className = 'markdown-attachment-copy';
  const name = document.createElement('strong'); name.textContent = attachment.name;
  const action = document.createElement('small');
  action.textContent = attachment.kind === 'markdown' ? '在线预览' : attachment.kind === 'text' || attachment.kind === 'pdf' ? '在浏览器中打开' : '下载文件';
  copy.append(name, action); card.append(icon, copy); card.onclick = () => onClick(attachment); return card;
}

function deletedAttachment(name: string, image: boolean): HTMLElement {
  const placeholder = document.createElement('span'); placeholder.className = image ? 'deleted-attachment deleted-image' : 'deleted-attachment';
  const title = document.createElement('strong'); title.textContent = name;
  const state = document.createElement('small'); state.textContent = '该文件已被删除';
  placeholder.append(title, state); return placeholder;
}

export function externalImageProxyUrl(value: string): string | null {
  const external = safeLink(value);
  return external?.startsWith('http') ? `/api/external-image?url=${encodeURIComponent(external)}` : null;
}

function externalAnchor(url: string): HTMLAnchorElement {
  const anchor = document.createElement('a'); anchor.href = url;
  if (url.startsWith('http')) { anchor.target = '_blank'; anchor.rel = 'noopener noreferrer'; anchor.referrerPolicy = 'no-referrer'; }
  return anchor;
}

function safeLink(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'mailto:' ? url.href : null;
  } catch { return null; }
}
